"use client";

import React, { useEffect, useState } from "react";
import {
  Grid,
  Flex,
  Box,
  Text,
  Spinner,
  Image as ChakraImage,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useGetModelCreationAsync } from "queries";
import { useRouter } from "next/navigation"; 
import { FaTrash } from "react-icons/fa";
import { PromptModal } from "components"; // Import the PromptModal component


type Generation = {
    sas_url: string | null;
    file_name: string | null;
    upscaled: boolean;
    status: string;
    prompt: string;
    nsfw?: boolean | null;
    isPlaceholder?: boolean;
    storage_type?: string;
  };
  

interface AsyncDisplayProps {
  modelId: string;
  isGenerating: boolean;
  generatedData?: { image_urls: string[] };
  numGenerations: number; 
  activeRequests : number;
  setActiveRequests: React.Dispatch<React.SetStateAction<number>>;
  
}

const AsyncDisplay: React.FC<AsyncDisplayProps> = ({ modelId, isGenerating, generatedData, numGenerations,   activeRequests,
  setActiveRequests }) => {
  const { data: generationsData, refetch, isLoading, isError } = useGetModelCreationAsync(9, modelId);
  const [displayedGenerations, setDisplayedGenerations] = useState<Generation[]>(
    []
  );
  const [pendingGenerations, setPendingGenerations] = useState<Generation[]>([]);
  const [updatedGenerations, setUpdatedGenerations] = useState<Generation[]>([]);
  const [selectedImage, setSelectedImage] = useState<Generation | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const router = useRouter();
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const { isOpen: isPromptModalOpen, onOpen: openPromptModal, onClose: closePromptModal } = useDisclosure();


  const POLLING_INTERVAL = 5000; 
  // Show placeholders when a request is made
  
   // Add placeholders when a request is initiated
   useEffect(() => {
    if (activeRequests > 0) {
      console.log(`Setting placeholders for ${activeRequests} images...`);

      const newPlaceholders = Array.from({ length: activeRequests }, (_, i) => ({
        sas_url: null,
        file_name: `placeholder-${Date.now()}-${i}`,
        upscaled: false,
        status: "placeholder",
        prompt: "Generating...",
        isPlaceholder: true,
      }));

      setDisplayedGenerations((prev) => {
        // Replace existing placeholders with new ones
        const filteredPrev = prev.filter((gen) => !gen.isPlaceholder);
        return [...newPlaceholders, ...filteredPrev].slice(0, 9); // Keep only the latest 9 items
      });
    }
  }, [activeRequests]);

  useEffect(() => {
    if (generatedData?.image_urls?.length) {
      setDisplayedGenerations((prev) => {
        const updatedList = [...prev];
  
        generatedData.image_urls.forEach((url) => {
          if (updatedList.some((gen) => gen.file_name === url)) return;
          const placeholderIndex = updatedList.findIndex((gen) => gen.isPlaceholder);
          if (placeholderIndex !== -1) {
            updatedList[placeholderIndex] = {
              sas_url: url,
              file_name: url,
              upscaled: false,
              status: "completed",
              prompt: "Generated Image",
            };
          } else {
            updatedList.unshift({
              sas_url: url,
              file_name: url,
              upscaled: false,
              status: "completed",
              prompt: "Generated Image",
            });
          }
        });
  
        return updatedList.slice(0, 9); // Keep the latest 9 generations
      });
  
      setActiveRequests(0);
      // ✅ **Refetch past generations once a new request completes**
      setTimeout(() => {
        refetch(); // 🚀 Fetch past creations again
      }, 2000); // Small delay to ensure new generations are processed
    }
  }, [generatedData, setActiveRequests, refetch]);
  

  // Fetch previous generations once
  useEffect(() => {
    if (generationsData && Array.isArray(generationsData.pages)) {
      const pastGenerations = generationsData.pages
        .flatMap((page: any) => page.image_generations || [])
        .filter((generation: Generation) => generation.status !== "pending" && generation.status !== "failed");
  
      setDisplayedGenerations((prev) => {
        const combined = [...pastGenerations, ...prev]; // ✅ **Merge old & new generations**
        return combined.slice(0, 9); // Keep the latest 9
      });
    }
  }, [generationsData]);
  

   // Poll for updates
   useEffect(() => {
    const fetchUpdates = async () => {
      try {
        const response = await fetch(`/api/generations/${modelId}`); // Replace with actual API endpoint
        const updatedData: Generation[] = await response.json();

        setDisplayedGenerations((prev) => {
          const updatedList = [...prev];

          updatedData.forEach((updatedGen) => {
            const placeholderIndex = updatedList.findIndex((gen) => gen.isPlaceholder);
            if (placeholderIndex !== -1) {
              updatedList[placeholderIndex] = {
                ...updatedGen,
                isPlaceholder: false,
              };
            }
          });

          return updatedList.slice(0, 9); // Limit to 9 items
        });

        setActiveRequests((prev) => Math.max(0, prev - updatedData.length));
      } catch (error) {
        console.error("Error fetching updates:", error);
      }
    };

    if (activeRequests > 0) {
      const interval = setInterval(fetchUpdates, POLLING_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [activeRequests, modelId, setActiveRequests]);
  

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="200px">
        <Text>Loading...</Text>
      </Flex>
    );
  }

  if (isError) {
    return (
      <Flex justifyContent="center" alignItems="center" height="200px">
        <Text>Error loading data. Please try again later.</Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" align="center" mt="10px">
      <Grid templateColumns={{ base: "repeat(3, 1fr)", md: "repeat(3, 1fr)" }} gap={{base: 3, md: 5}} width="100%" maxWidth="900px">
        {displayedGenerations.map((generation, index) => {
          const imageUrl = generation.file_name?.startsWith("https") ? generation.file_name : generation.sas_url;

          return (
            <Flex
              key={`generation-${index}`}
              border="0.5px solid gray"
              borderRadius={{base: "8px", md: "14px"}}
              sx={{ aspectRatio: "3 / 4" }}
              width="100%"
              align="center"
              justify="center"
              bg={generation.isPlaceholder ? "black" : "transparent"}
            >
              {generation.isPlaceholder ? (
                <Spinner size="lg" color="gray.500" bg="transparent" />
              ) : (
                imageUrl && (
                  <ChakraImage
                    src={imageUrl || undefined}
                    alt={generation.prompt || "Generated Image"}
                    borderRadius={{base: "8px", md: "14px"}}
                    objectFit="cover"
                    width="100%"
                    height="100%"
                    onClick={() => {
                      setSelectedImageUrl(imageUrl || "");
                      setSelectedImage(generation);
                      openPromptModal();
                    }}                    
                    _hover={{ cursor: "pointer" }}
                  />
                )
              )}
            </Flex>
          );
        })}
      </Grid>

      {selectedImage && (
        <PromptModal 
          isOpen={isPromptModalOpen} 
          onClose={closePromptModal} 
          isFlux={true} 
          model_id={modelId} 
          generationImage={selectedImageUrl || ''} 
          file_name={selectedImage.file_name || ''} 
          storage_type={selectedImage.storage_type}
        />
      )}
      {/* My Creations Button */}
      <Button
        mt={8}
        bg="transparent"
        borderRadius="full"
        w="100%"
        color="white"
        border="1px solid rgba(255, 255, 255, 0.5)"
        _hover={{
            bg: "rgba(255, 255, 255, 0.1)",
            borderColor: "rgba(255, 255, 255, 0.8)",
        }}
        onClick={() => router.push(`/creations/${modelId}`)}
        >
        My Creations
        </Button>
    </Flex>
  );
};
export default AsyncDisplay;

